/**
 * Cases: a user's own grouping of the actions and forms they are preparing for one matter in one
 * court (speck-opencourt/entities/Case.md). A case lives only in this browser's localStorage, like
 * wizard answers.
 *
 * A case's actions sit in envelopes (speck-opencourt/entities/Envelope.md): the papers filed with the
 * court together, in one submission, as an e-filing envelope holds them. Each action in an envelope has the
 * forms filed with it. Every form in a case is filed with one of its actions.
 *   { id, name, court, envelopes: [{ id, name, actions: [{ id, forms: [form ids] }] }], facts, archived, created, updated }
 * where `facts` is what kind of case it is, as its action wizards were told (see "case facts" below).
 * An action is in at most one envelope of a case, since its answers are kept once per case.
 *
 * An archived case is only filed under Archived in the case list; it keeps its answers.
 *
 * Each case has its own checklist, made by the case wizard below on the wizard engine, and each action
 * in it keeps its own answers and checklist, saved under the case (caseActionKey), apart from the same
 * action opened outside the case or in another case.
 */
import { checklist } from "./lib/civil.js";
import { WIZARDS } from "./actions/index.js";

const LIST_KEY = "opencourt:v2:cases";
export const caseKey = (caseId) => `opencourt:v2:case:${caseId}`;
export const caseActionKey = (caseId, actionId) => `${caseKey(caseId)}:${actionId}`;

/* ------------------------------------------------------------- store */
// A case saved before envelopes had a flat list of action ids (then called `processes`), and one saved before
// every form had an action a list of forms tied to none. Each action gets an envelope of its own, and each of those
// forms goes with the first action whose wizard links it, else the case's first action; with no action, it is
// dropped. A case saved while actions were called processes (#195) keeps each envelope's `processes` as its
// `actions`. Returns whether it changed the case.
function upgrade(c) {
  let changed = false;
  (c.envelopes || []).forEach((e) => {
    if (!Array.isArray(e.processes)) return;
    e.actions = e.processes;
    delete e.processes;
    changed = true;
  });
  if (Array.isArray(c.envelopes) && !Array.isArray(c.forms)) return changed;
  if (!Array.isArray(c.envelopes)) {
    c.envelopes = [];
    (c.processes || []).forEach((pid) => addAction(c, pid));
    delete c.processes;
  }
  const pids = caseActions(c);
  (c.forms || []).forEach((fid) => {
    const pid = pids.find((x) => WIZARDS[x] && WIZARDS[x].forms.includes(fid)) || pids[0];
    if (pid) addForm(c, fid, pid);
  });
  delete c.forms;
  return true;
}
// The case wizard's answers saved while actions were called processes (#195): the actions picked were `procs`, and
// the ticks of the case checklist's envelope sections were keyed "processes.<action id>". Renamed when the page loads,
// before a wizard reads them. (Each action's own answers are saved under caseActionKey, which the rename didn't change.)
(function renameCaseAnswers() {
  try {
    Object.keys(localStorage).filter((k) => k === "opencourt:v2:new-case" || /^opencourt:v2:case:[^:]+$/.test(k)).forEach((k) => {
      const s = JSON.parse(localStorage.getItem(k) || "null");
      if (!s || !s.a) return;
      const old = Object.keys(s.checks || {}).filter((x) => x.startsWith("processes."));
      if (!("procs" in s.a) && !old.length) return;
      if ("procs" in s.a) { s.a.acts = s.a.procs; delete s.a.procs; }
      old.forEach((x) => { s.checks[`actions.${x.slice("processes.".length)}`] = s.checks[x]; delete s.checks[x]; });
      localStorage.setItem(k, JSON.stringify(s));
    });
  } catch (e) { /* storage unavailable */ }
})();
function read() {
  try {
    const list = JSON.parse(localStorage.getItem(LIST_KEY) || "[]");
    if (!Array.isArray(list)) return [];
    // Saved once upgraded, so the new envelopes keep their ids.
    if (list.map(upgrade).some(Boolean)) write(list);
    return list;
  } catch (e) { return []; /* storage unavailable */ }
}
function write(list) {
  try { localStorage.setItem(LIST_KEY, JSON.stringify(list)); } catch (e) { /* storage unavailable */ }
}

/** Every saved case, most recently changed first. */
export const listCases = () => read().sort((a, b) => b.updated.localeCompare(a.updated));
export const getCase = (id) => read().find((c) => c.id === id) || null;

/** A new case in `court` (a courts.json id), named by the user. The name stays in this browser. */
export function createCase(name, court) {
  const now = new Date().toISOString();
  const c = { id: newId("c"), name, court, envelopes: [], archived: false, created: now, updated: now };
  write([...read(), c]);
  return c;
}

/** Applies `change(case)` to the saved case and saves it. */
export function updateCase(id, change) {
  const list = read();
  const c = list.find((x) => x.id === id);
  if (!c) return null;
  change(c);
  c.updated = new Date().toISOString();
  write(list);
  return c;
}

/** Deletes a case with its own answers and the answers of every action in it. */
export function deleteCase(id) {
  write(read().filter((c) => c.id !== id));
  try {
    Object.keys(localStorage).filter((k) => k === caseKey(id) || k.startsWith(`${caseKey(id)}:`)).forEach((k) => localStorage.removeItem(k));
  } catch (e) { /* storage unavailable */ }
}

export const addTo = (list, id) => (list.includes(id) ? list : [...list, id]);
const newId = (prefix) => `${prefix}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

/* ------------------------------------------------------------- envelopes */
// These read or change a case object; to save a change, call them inside updateCase(id, (c) => ...).

/** Every action id in case `c`, envelope by envelope. */
export const caseActions = (c) => c.envelopes.flatMap((e) => e.actions.map((p) => p.id));
/** The envelope of case `c` that holds action `pid`, or null. */
export const envelopeOf = (c, pid) => c.envelopes.find((e) => e.actions.some((p) => p.id === pid)) || null;
const entry = (c, pid) => { const e = envelopeOf(c, pid); return e ? e.actions.find((p) => p.id === pid) : null; };

/** Adds an empty envelope to case `c`, named after its place among them, and returns it. */
export function addEnvelope(c) {
  const used = new Set(c.envelopes.map((e) => e.name));
  let n = c.envelopes.length + 1;
  while (used.has(`Envelope ${n}`)) n++;
  const e = { id: newId("e"), name: `Envelope ${n}`, actions: [] };
  c.envelopes.push(e);
  return e;
}

/** Removes envelope `eid` from case `c` if it holds no action. */
export function removeEnvelope(c, eid) {
  c.envelopes = c.envelopes.filter((e) => e.id !== eid || e.actions.length);
}

/**
 * Adds action `pid` to envelope `eid` of case `c`, or to a new envelope of its own without one, with the
 * `forms` given. An action already in the case stays where it is.
 */
export function addAction(c, pid, eid = null, forms = []) {
  if (envelopeOf(c, pid)) return;
  const e = c.envelopes.find((x) => x.id === eid) || addEnvelope(c);
  e.actions.push({ id: pid, forms: [...forms] });
}

/** Removes action `pid` from case `c`, with the forms filed with it. */
export function removeAction(c, pid) {
  c.envelopes.forEach((e) => { e.actions = e.actions.filter((x) => x.id !== pid); });
}

/** Moves action `pid` of case `c`, with its forms, to envelope `eid`. */
export function moveAction(c, pid, eid) {
  const p = entry(c, pid), to = c.envelopes.find((e) => e.id === eid);
  if (!p || !to || to.actions.includes(p)) return;
  c.envelopes.forEach((e) => { e.actions = e.actions.filter((x) => x !== p); });
  to.actions.push(p);
}

/** Adds form `fid` to case `c`, filed with its action `pid`. */
export function addForm(c, fid, pid) {
  const p = entry(c, pid);
  if (p) p.forms = addTo(p.forms, fid);
}

/** Removes form `fid` from action `pid` of case `c`. */
export function removeForm(c, fid, pid) {
  const p = entry(c, pid);
  if (p) p.forms = p.forms.filter((f) => f !== fid);
}

/* ------------------------------------------------------------- case facts */
// What kind of case it is, which several action wizards ask, kept once per case so an action opened in the case
// starts with what another action's wizard was told (#170):
//   { cls: "unlimited" | "limitedOver10k" | "limitedUpTo10k", limited: "yes" | "no", collections: "yes" | "no",
//     ud: "yes" | "no", kind: "pi" | "contract" | "other" }
// An action module with a `caseFacts` export ({ from(facts) -> answers, to(answers) -> facts }) reads and records them.
// Each fact is known or missing: a wizard that doesn't ask one leaves it as it was.

/**
 * `facts` with the ones in `next` over them. Where a fact kept from before contradicts one in `next`, `next` wins:
 * a classification that isn't limited or unlimited as `limited` says is dropped, a collections case under
 * CRC 3.740 ($35,000 or less owed on credit) is a limited civil contract case and not an unlawful detainer.
 */
export function mergeFacts(facts, next) {
  next = Object.fromEntries(Object.entries(next).filter(([, v]) => v !== undefined));
  const f = { ...facts, ...next };
  const limitedCls = (cls) => (cls === "unlimited" ? "no" : "yes");
  if (f.cls && f.limited && limitedCls(f.cls) !== f.limited) delete f.cls;
  if (f.collections === "yes") {
    if ("collections" in next) {
      if (f.limited === "no") { delete f.limited; delete f.cls; }
      if (f.ud === "yes") delete f.ud;
      if (f.kind && f.kind !== "contract") delete f.kind;
    } else if (f.limited === "no" || f.ud === "yes" || (f.kind && f.kind !== "contract")) f.collections = "no";
  }
  return f;
}

// The facts in the answers of each action of case `c` that were saved before the case kept its facts.
function factsFromAnswers(c) {
  return caseActions(c).reduce((f, pid) => {
    const w = WIZARDS[pid];
    if (!w || !w.caseFacts) return f;
    try {
      const s = JSON.parse(localStorage.getItem(caseActionKey(c.id, pid)) || "null");
      return s && s.a ? mergeFacts(f, w.caseFacts.to(s.a)) : f;
    } catch (e) { return f; /* storage unavailable */ }
  }, {});
}

/** What case `c` (a saved case) is known to be, from the wizards of its actions. */
export const caseFacts = (c) => c.facts || factsFromAnswers(c);

/** Records `next`, facts from an action wizard's answers, on case `id`; it saves only when they change something. */
export function saveCaseFacts(id, next) {
  const c = getCase(id);
  if (!c) return;
  const f = mergeFacts(caseFacts(c), next);
  if (JSON.stringify(f) !== JSON.stringify(c.facts)) updateCase(id, (x) => { x.facts = f; });
}

/* ------------------------------------------------------------- case wizard */
const ROLE = {
  plaintiff: "Plaintiff or petitioner",
  defendant: "Defendant or respondent",
  nonparty: "Not a party",
};
// Why a non-party is filing in the case.
const REASON = {
  subpoena: "A subpoena: you were served with one, or one served on someone else affects you",
  intervene: "To join the case as a party (intervention)",
  unseal: "To ask the court to unseal a record",
  claim: "To claim property levied on under a writ in the case (third-party claim)",
  other: "Another reason",
};
const STAGE = {
  plaintiff: { start: "Starting: the complaint isn't filed yet", progress: "Under way: the complaint is filed", ending: "Ending: settling or dismissing it" },
  defendant: { start: "Just served with the summons and complaint", progress: "Under way: a response is filed", ending: "Ending: settling it" },
};
// Actions a case suggests for a side and stage, among the court's actions that have a wizard.
const SUGGESTED = {
  plaintiff: { start: ["complaint", "service-of-summons"], progress: ["case-management-statement"], ending: ["request-for-dismissal"] },
  defendant: { start: ["answer"], progress: ["case-management-statement"], ending: [] },
  nonparty: { subpoena: ["motion-to-quash-subpoena"], intervene: ["intervention"], unseal: ["motion-to-unseal-records"], claim: ["third-party-claim-of-ownership-or-possession"], other: [] },
};
const suggested = (a) => (SUGGESTED[a.role] || {})[a.role === "nonparty" ? a.reason : a.stage] || [];

/**
 * The case wizard for one case, as a module for the wizard engine (src/wizard.js). `actions` are the
 * court's actions, `forms` its forms, `wizards` the action modules (src/actions/index.js) and
 * `level` the court's level and `system` its system (courts.json): the case-wide items come from California's
 * trial court rules, so a reviewing court's case checklist, or one in another state, lists only its actions and forms.
 * The checklist reads the saved case, so actions and forms added on the case page show in it too.
 */
export function caseWizard(caseId, { actions, forms, overrides = [], wizards, level, system }) {
  const californiaTrial = level === "general_trial" && system === "ca";
  const byId = (list, id) => list.find((x) => x.id === id);
  const withWizard = actions.filter((p) => wizards[p.id]);
  const formLabel = (f) => [f.form_number, f.title].filter(Boolean).join(" ");
  // The forms the chosen actions can use: the ones a wizard's checklist links, and the ones forms.json ties to the action.
  const formsFor = (pids) => forms.filter((f) => pids.some((pid) => (wizards[pid] && wizards[pid].forms.includes(f.id)) || (f.actions || []).includes(pid)));
  // A mandatory form (the issuer's, or the court's by a local rule in courts.json form_overrides) is listed first,
  // tagged, and checked when it is first offered.
  const localRule = (f) => (overrides || []).find((o) => o.form === f.id && o.authority === "must_use");
  const mandatory = (f) => f.authority === "must_use" || !!localRule(f);
  const formOpts = (a) => {
    const list = formsFor(a.acts || []);
    return [...list.filter(mandatory), ...list.filter((f) => !mandatory(f))].map((f) => {
      const o = localRule(f);
      return mandatory(f)
        ? { v: f.id, l: formLabel(f), tag: "Mandatory", on: true, h: f.authority !== "must_use" && o ? `Mandatory in this court (${o.rule}) ${o.when}.` : null }
        : { v: f.id, l: formLabel(f) };
    });
  };

  const steps = [
    {
      id: "side",
      title: "Your side of the case",
      intro: "These answers pick the actions to suggest and the deadlines that run through the whole case. The wizard doesn't ask for names or case details.",
      fields: [
        { k: "role", type: "radio", label: "Who are you preparing papers for?", req: true, opts: Object.entries(ROLE).map(([v, l]) => ({ v, l })) },
        {
          k: "stage", type: "radio", label: "Where is the case now?", req: true,
          show: (a) => californiaTrial && (a.role === "plaintiff" || a.role === "defendant"),
          opts: (a) => Object.entries(STAGE[a.role] || {}).map(([v, l]) => ({ v, l })),
        },
        {
          k: "reason", type: "radio", label: "Why are you filing in the case?", req: true,
          show: (a) => a.role === "nonparty",
          opts: Object.entries(REASON).map(([v, l]) => ({ v, l })),
        },
      ],
    },
    {
      id: "actions",
      title: "Actions",
      intro: (a, court) => `Pick the actions you expect to prepare. Each gets its own checklist in this case and its own envelope, for the papers you file with the court at one time; on the case page you can put actions you file together in one envelope. Actions with a wizard are listed, with any other suggested for your side; pick None of these to search the other actions ${court.name} allows, or add them later from the case page.`,
      fields: [{
        k: "acts", type: "check", label: "Which actions will you prepare?",
        hint: "Suggested for your side and stage of the case are marked.",
        opts: (a) => {
          const s = suggested(a);
          const rank = (p) => (s.includes(p.id) ? s.indexOf(p.id) : s.length);
          const listed = [...withWizard, ...actions.filter((p) => !wizards[p.id] && s.includes(p.id))];
          return listed.sort((x, y) => rank(x) - rank(y)).map((p) => ({ v: p.id, l: p.name, tag: s.includes(p.id) ? "Suggested" : null }));
        },
        // "None of these" searches the court's other actions, the ones not listed.
        none: "None of these",
        searchLabel: "Search the court's other actions",
        more: actions.map((p) => ({ v: p.id, l: p.name, s: (p.aliases || []).join(" ") })),
      }],
    },
    {
      id: "forms",
      title: "Forms",
      intro: "The forms the checklists of those actions can link. Each is filed with an action that uses it. Mandatory forms are checked to begin with. Pick the ones you expect to use; you can add any other form to an action from the case page.",
      skipIf: (a) => !formOpts(a).length,
      fields: [{ k: "forms", type: "check", label: "Which forms will you use?", opts: formOpts }],
    },
  ];

  const names = (ids, list, label) => ids.map((id) => byId(list, id)).filter(Boolean).map(label).join("; ") || "None";
  function summary(a) {
    return [
      ["Your side", ROLE[a.role] || ""],
      ...(STAGE[a.role] && a.stage ? [["Stage", STAGE[a.role][a.stage] || ""]] : []),
      ...(a.role === "nonparty" && a.reason ? [["Filing for", REASON[a.reason] || ""]] : []),
      ["Actions", names(a.acts || [], actions, (p) => p.name)],
      ["Forms", names((a.forms || []).filter((id) => formOpts(a).some((o) => o.v === id)), forms, formLabel)],
    ];
  }

  function build(a, court) {
    const c = checklist();
    const trial = californiaTrial;
    const party = trial && (a.role === "plaintiff" || a.role === "defendant");
    const active = party && a.stage !== "ending";
    c.row("Court", court.name);
    c.row("Your side", ROLE[a.role] || "");
    if (STAGE[a.role] && a.stage) c.row("Stage", STAGE[a.role][a.stage] || "");
    if (a.role === "nonparty" && a.reason) c.row("Filing for", REASON[a.reason] || "");

    const all = c.section("throughout", "Throughout the case", "What applies to every paper in the case, whichever action it belongs to.");
    if (trial) c.item(all, "format", "Put every paper you file in the format the rules require, including the first page layout: attorney or party information, the court, the title of the case, the case number and the nature of the paper.", "CRC 2.100-2.119; 2.111");
    if (party) {
      c.item(all, "serve", "Serve papers on the other parties personally, by mail, or electronically where that is allowed.", "CCP 1010.6; 1011; 1013");
      c.item(all, "mail", "When a paper is served on you by mail, a deadline to respond to it runs 5 more calendar days if the mailing and the address are both in California, 10 if either is elsewhere in the United States, and 20 if either is outside it. The extension doesn't apply to a notice of appeal or a notice of intention to move for a new trial or to vacate a judgment.", "CCP 1013(a)");
      c.court(all, "efile", court, "efiling.represented", "local rules on who must e-file (CRC 2.253(b) lets a court require represented parties to)");
    }

    const dl = c.section("deadlines", "Deadlines that run through the case",
      party ? "For a general civil case. CRC 3.110 doesn't apply to collections cases, unlawful detainers or Family Code proceedings (CRC 3.110(a)), and a court can exempt some kinds of case from the case management rules by local rule (CRC 3.720(b)). Each action checklist says which apply and computes the dates." : "");
    if (party && a.role === "plaintiff" && a.stage !== "ending") {
      c.item(dl, "serve60", "Serve the summons and complaint on every named defendant and file the proofs of service within 60 days after filing the complaint.", "CRC 3.110(b)");
      c.item(dl, "serve3y", "In any case, serve the summons and complaint on each defendant within three years after the action is commenced against that defendant.", "CCP 583.210(a)");
      c.item(dl, "trial5y", "Bring the action to trial within five years after it is commenced against the defendant.", "CCP 583.310");
    }
    if (party && a.role === "defendant" && a.stage === "start") {
      c.item(dl, "respond", "File a written response to the complaint within 30 days after the summons is served on you.", "CCP 412.20(a)(3)");
    }
    if (active) {
      c.item(dl, "cms", "File and serve a case management statement no later than 15 calendar days before each case management conference or review.", "CRC 3.725(a)");
    }
    const nonparty = trial && a.role === "nonparty";
    // Answers saved before the reason was asked were all about a subpoena.
    const reason = a.reason || "subpoena";
    if (nonparty && reason === "subpoena") {
      c.item(dl, "quash", "You can ask the court to quash the subpoena or modify it, or for a protective order, by a motion made reasonably and in good faith before the time to comply.", "CCP 1987.1(a)");
    }
    if (nonparty && reason === "intervene") {
      c.item(dl, "intervene", "Ask the court for leave to intervene by noticed motion or ex parte application, with a copy of the proposed complaint in intervention or answer in intervention and the grounds for intervening. The court permits intervention only on a timely application, so don't wait.", "CCP 387(c)-(d)");
      c.item(dl, "intervene-serve", "If leave is granted, separately file the complaint or answer in intervention and serve it with the order or notice granting leave: on a party that hasn't appeared, as a summons is served; on a party that has, that way or as other papers are served. The parties then have 30 days after service to respond to it.", "CCP 387(e)-(f)");
    }
    if (nonparty && reason === "unseal") {
      c.item(dl, "unseal", "A member of the public may move, apply or petition to unseal a record. File notice of it and serve it on all parties in the case.", "CRC 2.551(h)(2)");
    }
    if (nonparty && reason === "claim") {
      c.item(dl, "claim", "File the third-party claim with the levying officer, with two copies, after the levy but before the officer sells the property, delivers it to the creditor or pays the proceeds of collection to the creditor. The claim is available only if your interest is superior to the creditor's lien.", "CCP 720.110; 720.120");
    }

    // One section per envelope, each action followed by the forms filed with it. The item keys don't name the
    // envelope, so moving an action keeps its ticks; a form in more than one action is listed once.
    const saved = getCase(caseId) || { envelopes: [] };
    const listed = new Set();
    const formItem = (s, f) => { if (!listed.has(f.id)) { listed.add(f.id); c.item(s, f.id, `Prepare ${formLabel(f)}.`, "", "", [f.id]); } };
    saved.envelopes.forEach((e) => {
      const s = c.section("actions", e.name, "Filed together, in one submission to the court.");
      e.actions.forEach((ep) => {
        const p = byId(actions, ep.id);
        if (!p) return;
        c.item(s, p.id, wizards[p.id] ? `${p.name}: work through its checklist.` : `${p.name}: no wizard yet; read its sources on the action page.`);
        ep.forms.map((id) => byId(forms, id)).filter(Boolean).forEach((f) => formItem(s, f));
      });
    });

    if (!trial) c.verify(system === "ca" ? `For ${court.name}, check the rules for papers filed in it and their deadlines (California Rules of Court, Title 8, and the court's local rules).`
      : `For ${court.name}, check the rules for papers filed in it and their deadlines (the statewide rules of court and statutes under Documents, and the court's local rules).`);
    c.verify(`For ${court.name}, check the local rules that apply to every paper filed there.`);
    return c.result("Case checklist");
  }

  return { id: "case", steps, summary, build, suggested };
}
